import { Button } from '@/components/ui/button'
import { RefreshCw, WifiOff } from 'lucide-react'
import { useEffect, useState } from 'react'

/**
 * Full-screen offline notice. App updates are handled separately by the
 * UpdateBanner (see use-app-update.ts) - this component only watches connectivity.
 */
export function PWAStatus() {
  const [isOnline, setIsOnline] = useState(true)

  useEffect(() => {
    const verifyConnection = async () => {
      // Some browsers (including Brave in certain setups) can report navigator.onLine=false
      // even when same-origin requests still succeed.
      if (navigator.onLine) {
        setIsOnline(true)
        return
      }

      try {
        const response = await fetch('/favicon.ico', {
          method: 'HEAD',
          cache: 'no-store',
        })
        setIsOnline(response.ok)
      } catch {
        setIsOnline(false)
      }
    }

    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => {
      void verifyConnection()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    void verifyConnection()

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Show no internet message when offline
  if (!isOnline) {
    return (
      <div className='fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300'>
        <div className='flex flex-col max-w-[420px] w-full bg-white dark:bg-card rounded-lg shadow-xl border relative overflow-hidden animate-in zoom-in-95 duration-300'>
          {/* Header */}
          <div className='flex items-center justify-between p-6 border-b dark:border-gray-700'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center'>
                <WifiOff className='w-5 h-5 text-red-600 dark:text-red-400' />
              </div>
              <h2 className='text-lg text-gray-900 dark:text-white'>No Internet Connection</h2>
            </div>
          </div>

          {/* Content */}
          <div className='p-6'>
            <p className='text-gray-700 text-left dark:text-gray-300 mb-4'>
              Please check your internet connection and try again. The app requires an active internet connection to
              function properly.
            </p>

            <div className='space-y-3'>
              <div className='flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg'>
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                <p className='text-sm text-red-800 dark:text-red-200 font-medium'>
                  Check your WiFi or mobile data connection
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className='px-6 pb-6 flex gap-3 justify-end'>
            <Button variant='outline' onClick={() => window.location.reload()} className='flex-1'>
              <RefreshCw className='h-4 w-4' />
              Retry Connection
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
